import React, { useState } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  IconButton, 
  Menu, 
  MenuItem, 
  Box, 
  Avatar, 
  Tooltip,
  useMediaQuery,
  useTheme,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider
} from '@mui/material';
import { 
  Menu as MenuIcon, 
  Translate as TranslateIcon,
  Dashboard as DashboardIcon,
  VideoLibrary as VideoLibraryIcon,
  History as HistoryIcon,
  Settings as SettingsIcon,
  Login as LoginIcon,
  Logout as LogoutIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { OutputLanguage } from '../services/contentService';

// Language options
const languages: { value: OutputLanguage; label: string }[] = [
  { value: OutputLanguage.English, label: 'English' },
  { value: OutputLanguage.Hindi, label: 'Hindi' },
  { value: OutputLanguage.Hinglish, label: 'Hinglish' },
  { value: OutputLanguage.Brazilian, label: 'Brazilian Portuguese' },
  { value: OutputLanguage.Brazinglish, label: 'Brazinglish' },
  { value: OutputLanguage.Marathi, label: 'Marathi' },
  { value: OutputLanguage.Marathinglish, label: 'Marathinglish' }
];

interface HeaderProps {
  onLanguageChange: (language: OutputLanguage) => void;
  currentLanguage: OutputLanguage;
}

const Header: React.FC<HeaderProps> = ({ onLanguageChange, currentLanguage }) => {
  const { currentUser, userData, signOut } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // State for language menu
  const [languageAnchorEl, setLanguageAnchorEl] = useState<null | HTMLElement>(null);
  const languageMenuOpen = Boolean(languageAnchorEl);
  
  // State for user menu
  const [userAnchorEl, setUserAnchorEl] = useState<null | HTMLElement>(null);
  const userMenuOpen = Boolean(userAnchorEl);
  
  // State for mobile drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  
  // Handle language menu open
  const handleLanguageMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setLanguageAnchorEl(event.currentTarget);
  };
  
  // Handle language menu close
  const handleLanguageMenuClose = () => {
    setLanguageAnchorEl(null);
  };
  
  // Handle language selection
  const handleLanguageSelect = (language: OutputLanguage) => {
    onLanguageChange(language);
    handleLanguageMenuClose();
  };
  
  // Handle user menu open
  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserAnchorEl(event.currentTarget);
  };
  
  // Handle user menu close
  const handleUserMenuClose = () => {
    setUserAnchorEl(null);
  };
  
  // Handle sign out
  const handleSignOut = async () => {
    try {
      await signOut();
      handleUserMenuClose();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };
  
  // Toggle mobile drawer
  const toggleDrawer = (open: boolean) => {
    setDrawerOpen(open);
  };
  
  // Get subscription badge color
  const getSubscriptionColor = () => {
    if (!userData) return theme.palette.grey[500];
    
    const subscription = userData.subscription || 'free';
    
    switch (subscription) {
      case 'premium':
        return theme.palette.secondary.main;
      case 'basic':
        return theme.palette.primary.main;
      default:
        return theme.palette.grey[500];
    }
  };
  
  // Mobile drawer content
  const drawerContent = (
    <Box
      sx={{ width: 250 }}
      role="presentation"
      onClick={() => toggleDrawer(false)}
      onKeyDown={() => toggleDrawer(false)}
    >
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Typography
          variant="h6"
          component={RouterLink}
          to="/"
          sx={{
            textDecoration: 'none',
            color: 'inherit',
            fontWeight: 700,
            mb: 1
          }}
        >
          <span className="gradient-text">SharkPanzer</span>
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Video Capsules, Not Summaries
        </Typography>
      </Box>
      
      <Divider />
      
      <List>
        <ListItem disablePadding>
          <ListItemButton component={RouterLink} to="/">
            <ListItemIcon>
              <DashboardIcon />
            </ListItemIcon>
            <ListItemText primary="Dashboard" />
          </ListItemButton>
        </ListItem>
        
        <ListItem disablePadding>
          <ListItemButton component={RouterLink} to="/create">
            <ListItemIcon>
              <VideoLibraryIcon />
            </ListItemIcon>
            <ListItemText primary="Create Capsule" />
          </ListItemButton>
        </ListItem>
        
        {currentUser && (
          <ListItem disablePadding>
            <ListItemButton component={RouterLink} to="/history">
              <ListItemIcon>
                <HistoryIcon />
              </ListItemIcon>
              <ListItemText primary="Capsule History" />
            </ListItemButton>
          </ListItem>
        )}
        
        {currentUser && (
          <ListItem disablePadding>
            <ListItemButton component={RouterLink} to="/settings">
              <ListItemIcon>
                <SettingsIcon />
              </ListItemIcon>
              <ListItemText primary="Settings" />
            </ListItemButton>
          </ListItem>
        )}
      </List>
      
      <Divider />
      
      <List>
        <ListItem disablePadding>
          <ListItemButton onClick={handleLanguageMenuOpen}>
            <ListItemIcon>
              <TranslateIcon />
            </ListItemIcon>
            <ListItemText primary={`Language: ${currentLanguage}`} />
          </ListItemButton>
        </ListItem>
        
        {!currentUser ? (
          <ListItem disablePadding>
            <ListItemButton component={RouterLink} to="/login">
              <ListItemIcon>
                <LoginIcon />
              </ListItemIcon>
              <ListItemText primary="Login" />
            </ListItemButton>
          </ListItem>
        ) : (
          <ListItem disablePadding>
            <ListItemButton onClick={handleSignOut}>
              <ListItemIcon>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </ListItemButton>
          </ListItem>
        )}
      </List>
    </Box>
  );
  
  return (
    <>
      <AppBar position="fixed" elevation={0}>
        <Toolbar>
          {isMobile ? (
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={() => toggleDrawer(true)}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          ) : null}
          
          <Typography
            variant="h6"
            component={RouterLink}
            to="/"
            sx={{
              textDecoration: 'none',
              color: 'inherit',
              fontWeight: 700,
              flexGrow: isMobile ? 1 : 0,
              mr: 2
            }}
          >
            <span className="gradient-text">SharkPanzer</span>
          </Typography>
          
          {!isMobile && (
            <>
              <Button 
                color="inherit" 
                component={RouterLink} 
                to="/"
                sx={{ mr: 1 }}
              >
                Dashboard
              </Button>
              
              <Button 
                color="inherit" 
                component={RouterLink} 
                to="/create"
                sx={{ mr: 1 }}
              >
                Create Capsule
              </Button>
              
              {currentUser && (
                <Button 
                  color="inherit" 
                  component={RouterLink} 
                  to="/history"
                  sx={{ mr: 1 }}
                >
                  Capsule History
                </Button>
              )}
              
              <Box sx={{ flexGrow: 1 }} />
            </>
          )}
          
          <Tooltip title="Change language">
            <IconButton
              onClick={handleLanguageMenuOpen}
              size="large"
              edge="end"
              color="inherit"
              aria-label="select language"
              aria-controls="language-menu"
              aria-haspopup="true"
              sx={{ ml: 1 }}
            >
              <TranslateIcon />
            </IconButton>
          </Tooltip>
          
          {currentUser ? (
            <Tooltip title={userData?.email || 'User'}>
              <IconButton
                onClick={handleUserMenuOpen}
                size="small"
                edge="end"
                aria-label="account of current user"
                aria-controls="user-menu"
                aria-haspopup="true"
                sx={{ ml: 1 }}
              >
                <Avatar 
                  sx={{ 
                    width: 32, 
                    height: 32,
                    border: `2px solid ${getSubscriptionColor()}`
                  }}
                >
                  {currentUser.email?.charAt(0).toUpperCase() || 'U'}
                </Avatar>
              </IconButton>
            </Tooltip>
          ) : (
            <Button 
              color="inherit" 
              component={RouterLink} 
              to="/login"
              sx={{ ml: 1 }}
            >
              Login
            </Button>
          )}
        </Toolbar>
      </AppBar>
      
      {/* Language Menu */}
      <Menu
        id="language-menu"
        anchorEl={languageAnchorEl}
        open={languageMenuOpen}
        onClose={handleLanguageMenuClose}
        MenuListProps={{
          'aria-labelledby': 'language-button',
        }}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {languages.map((language) => (
          <MenuItem 
            key={language.value} 
            onClick={() => handleLanguageSelect(language.value)}
            selected={currentLanguage === language.value}
          >
            {language.label}
          </MenuItem>
        ))}
      </Menu>
      
      {/* User Menu */}
      <Menu
        id="user-menu"
        anchorEl={userAnchorEl}
        open={userMenuOpen}
        onClose={handleUserMenuClose}
        MenuListProps={{
          'aria-labelledby': 'user-button',
        }}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem component={RouterLink} to="/profile" onClick={handleUserMenuClose}>
          Profile
        </MenuItem>
        <MenuItem component={RouterLink} to="/settings" onClick={handleUserMenuClose}>
          Settings
        </MenuItem>
        <MenuItem component={RouterLink} to="/subscription" onClick={handleUserMenuClose}>
          Subscription
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleSignOut}>
          Logout
        </MenuItem>
      </Menu>
      
      {/* Mobile Drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => toggleDrawer(false)}
      >
        {drawerContent}
      </Drawer>
      
      {/* Toolbar spacer */}
      <Toolbar />
    </>
  );
};

export default Header;
